import { lighten, opacify } from 'polished';
import styled, { css } from 'styled-components';

const StyledTable = styled.div<{ $mobileCardLayout?: boolean; $isRefreshing?: boolean }>`
  min-width: 0;
  width: 100%;
  input,
  select {
    ${(p) => p.theme.tableInputBorder}
  }
  table {
    width: 100%;
    margin-bottom: ${(p) => p.theme.space[3]};
    border-collapse: collapse;
    tbody {
      tr {
        border-bottom: 2px solid ${(p) => p.theme.colors.gray200};
        &:nth-child(odd) {
          background-color: ${(p) => opacify(0.5, p.theme.colors.gray100)};
        }
        &:nth-child(even) {
          background-color: ${(p) => p.theme.colors.light};
        }
        &.selected {
          background-color: ${(p) => lighten(0.25, p.theme.colors.secondary200)};
        }
      }

      ${(p) =>
        p.$isRefreshing &&
        css`
          tr:not(.table-status-row) {
            opacity: 0.65;
            pointer-events: none;
          }
        `}

      tr.table-status-row {
        background: ${(p) => p.theme.colors.light};

        td {
          padding: 0;
        }
      }
    }
    th {
      text-align: left;
      color: ${(p) => p.theme.colors.gray300};
      border-bottom: 2px solid ${(p) => p.theme.colors.gray200};
      div {
        position: relative;
        &.pointer {
          cursor: pointer;
          user-select: none;
        }
        .sort-icon {
          position: absolute;
          right: 0;
          width: 20px;
          height: 20px;
          &:before,
          &:after,
          .asc,
          .desc {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: block;
            height: 0;
            width: 0;
            border: 5px solid transparent;
          }
          &:before {
            border-bottom-color: ${(p) => p.theme.colors.gray100};
            margin-top: -8px;
          }
          &:after {
            border-top-color: ${(p) => p.theme.colors.gray100};
            margin-top: 6px;
          }
          .asc {
            margin-top: -8px;
            border-bottom-color: #000;
          }
          .desc {
            margin-top: 6px;
            border-top-color: #000;
            z-index: 1;
          }
        }
      }
    }
    th,
    td {
      margin: 0;
      padding: 0.5rem;
    }
  }

  ${(p) =>
    p.$mobileCardLayout &&
    css`
      @media (max-width: 639px) {
        > div:first-child {
          align-items: stretch;
          gap: 10px;
          margin-bottom: 12px;

          > div:last-child {
            flex: 1;
            min-width: 0;
          }
        }

        table {
          display: block;
          min-width: 0;
          margin-bottom: 12px;

          thead {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }

          tbody {
            display: grid;
            gap: 12px;
          }

          tbody tr,
          tbody tr:nth-child(odd),
          tbody tr:nth-child(even) {
            display: block;
            padding: 16px 12px;
            border: 0;
            border-radius: 12px;
            background: ${(p) => p.theme.colors.light};
          }

          tbody tr:nth-child(odd) {
            background: ${(p) => p.theme.colors.gray000};
          }

          tbody tr.table-status-row,
          tbody tr.table-status-row:nth-child(odd),
          tbody tr.table-status-row:nth-child(even) {
            background: ${(p) => p.theme.colors.light};
            padding: 0;

            td {
              display: block;
              padding: 0;
            }

            td::before {
              content: none;
            }
          }

          th,
          td {
            width: auto !important;
            padding: 4px 0;
            border: 0;
            font-size: 14px;
            line-height: 1.3;
          }

          td {
            display: grid;
            grid-template-columns: 108px minmax(0, 1fr);
            gap: 12px;
            overflow-wrap: anywhere;
          }

          td::before {
            content: attr(data-label);
            color: ${(p) => p.theme.colors.gray300};
          }

          td:last-child button {
            width: 100%;
          }
        }
      }
    `}
`;

const TableScroll = styled.div<{ $mobileCardLayout?: boolean }>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  -webkit-overflow-scrolling: touch;

  table {
    min-width: var(--data-table-min-width, 100%);
  }

  ${(p) =>
    p.$mobileCardLayout &&
    css`
      @media (max-width: 639px) {
        overflow: visible;

        table {
          min-width: 0;
        }
      }
    `}
`;

const Pagination = styled.div<{ $mobileCardLayout?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
  .total {
    font-size: ${(p) => p.theme.fontSizes.sm};
    color: ${(p) => p.theme.colors.gray200};
  }
  .pages {
    display: flex;
    gap: 40px;
    flex-wrap: wrap;
    min-width: 0;
    .display {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .change-page {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      button {
        width: 40px;
        height: 32px;
        background-color: ${(p) => p.theme.colors.gray100};
        border-radius: ${(p) => p.theme.radii.base};
        :hover:enabled {
          background-color: ${(p) => p.theme.colors.primary400};
          color: ${(p) => p.theme.colors.light};
          cursor: pointer;
        }
        :active:enabled {
          background-color: ${(p) => p.theme.colors.primary500};
        }
        :focus-visible {
          outline: 3px solid ${(p) => p.theme.colors.secondary500};
          outline-offset: 2px;
        }
      }
      span {
        display: flex;
        gap: 2px;
        align-items: center;
      }
      input {
        width: 50px;
        padding: 3px 5px;
      }
      label {
        display: flex;
        align-items: center;
        gap: 2px;
      }
    }
  }

  ${(p) =>
    p.$mobileCardLayout &&
    css`
      @media (max-width: 639px) {
        align-items: center;
        flex-direction: column;
        gap: 10px;
        color: ${(p) => p.theme.colors.gray300};
        font-size: 14px;
        text-align: center;

        .pages {
          align-items: center;
          flex-direction: column;
          gap: 10px;

          .change-page {
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px;

            button {
              width: 34px;
              height: 30px;
            }

            > span:last-child {
              display: none;
            }
          }
        }
      }
    `}
`;

export { StyledTable, TableScroll, Pagination };
